// src/pages/ChatAnimatorPage.js
// Page for the Chat Animator Tool (V21 - Audio Previews)

import React, { useState, useEffect, useRef, useCallback } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import OpenAI from "openai";

import { vibePresets } from "../data/vibePresets";
import { defaultScript as initialDefaultScript } from "../data/defaultScript";
import { voices, soundEffects as availableSoundEffects } from "../data/constants";
import { sleep, audioDuration, playAudioFromBlob } from "../data/helpers";
import './ChatAnimatorPage.css';

// --- Default dark mode colors ---
const defaultColors = {
    headerColor: '#1C1C1E', chatBg: '#000000', titleText: '#FFFFFF',
    text: '#FFFFFF', sent: '#0B93F6', received: '#3A3A3C', svgIcon: '#0B93F6'
};

// --- Reusable UI Input Components ---
const ColorInput = ({ label, value, onChange, id }) => ( <div><label htmlFor={id} className="label">{label}</label><input id={id} type="color" value={value} onChange={onChange} className="color-input-box"/></div> );
const TextInput = ({ label, value, onChange, id, type="text", placeholder="" }) => ( <div className="flex-1"><label htmlFor={id} className="label">{label}</label><input id={id} type={type} value={value} placeholder={placeholder} onChange={onChange} className="input-base"/></div> );
const SelectInput = ({ label, value, onChange, options, id }) => ( <div className="flex-1"><label htmlFor={id} className="label">{label}</label><select id={id} value={value} onChange={onChange} className="input-base">{options.map((opt) => (<option key={opt.value || opt} value={opt.value || opt}>{opt.label || opt}</option>))} </select></div> );
const RangeInput = ({ label, value, min, max, step, onChange, id, unit }) => ( <div><label htmlFor={id} className="label-xs">{label}: {value}{unit}</label><input id={id} type="range" min={min} max={max} step={step || 1} value={value} onChange={onChange} className="range-input" /></div> );

// --- Helper to create a unique ID ---
const newId = () => Date.now() + Math.random();

// --- This helper now formats the vibe details as per OpenAI's 'instructions' parameter format ---
const createInstructionsFromVibe = (vibe) => {
    if (!vibe) return undefined;
    const instructions = Object.entries(vibe)
        .filter(([key, value]) => key !== 'open' && key !== 'preset' && value)
        .map(([key, value]) => {
            const formattedKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
            return `${formattedKey}: ${value}`;
        })
        .join('\n\n');
    return instructions || undefined;
};

// --- Main Page Component ---
export default function ChatAnimatorPage() {
    // --- State Declarations ---
    const [colorsData, setColorsData] = useState(defaultColors);
    const [scriptData, setScriptData] = useState(
        initialDefaultScript.map(msg => ({
            ...msg,
            id: newId(),
            collapsed: true,
            speed: 1.0,
            vibe: msg.type === "text" ? { ...vibePresets["No Vibe"], ...msg.vibe, preset: msg.vibe?.preset || "No Vibe", open: false } : undefined
        }))
    );
    const [editorLog, setEditorLog] = useState(["Ready to animate!"]);
    const [chatTitle, setChatTitle] = useState("The Boss");
    const [profileImageSrc, setProfileImageSrc] = useState("/textAppImg/dog.jpg");
    const [profileImage, setProfileImage] = useState(null);
    const [openaiKey, setOpenaiKey] = useState(process.env.REACT_APP_OPENAI_API_KEY || "");
    const [openaiInstance, setOpenaiInstance] = useState(null);

    const [phoneUiOffsetY, setPhoneUiOffsetY] = useState(0);
    const [phoneUiWidth, setPhoneUiWidth] = useState(74);
    const [phoneUiHeight, setPhoneUiHeight] = useState(44);
    const [headerPaddingScale, setHeaderPaddingScale] = useState(150);
    const [iconOffsetY, setIconOffsetY] = useState(-20);
    const [bubbleRoundness, setBubbleRoundness] = useState(50);
    const [currentFontSizeMultiplier, setCurrentFontSizeMultiplier] = useState(1.5);
    const [pfpSizeMultiplier, setPfpSizeMultiplier] = useState(1.6);
    const [iconSizeMultiplier, setIconSizeMultiplier] = useState(1.6);
    const [imageSizeMultiplier, setImageSizeMultiplier] = useState(0.7);

    const [appBackgroundColor, setAppBackgroundColor] = useState("#00EB04");
    const [isProcessing, setIsProcessing] = useState(false);
    const [processingStatus, setProcessingStatus] = useState('');
    const [showSpeakerIcon, setShowSpeakerIcon] = useState(false);
    const [playSendSFX, setPlaySendSFX] = useState(true);
    const [headerIcons, setHeaderIcons] = useState({ back: null, camera: null });

    const [showHeaderBubble, setShowHeaderBubble] = useState(false);
    const [headerBubbleText, setHeaderBubbleText] = useState("Following?");
    const [headerBubbleColor, setHeaderBubbleColor] = useState('#0B93F6');
    const [headerBubbleOffsetX, setHeaderBubbleOffsetX] = useState(0);
    
    // ✨ NEW: State to track which audio preview is currently playing
    const [previewingId, setPreviewingId] = useState(null);


    const canvasRef = useRef(null);
    const preloadedAudio = useRef(new Map());
    const preloadedImages = useRef(new Map());
    const animationAbortController = useRef(null);

    const log = useCallback((msg) => setEditorLog((p) => [...p.slice(-19), msg]), []);

    useEffect(() => {
        try { if (openaiKey) setOpenaiInstance(new OpenAI({ apiKey: openaiKey, dangerouslyAllowBrowser: true })); else setOpenaiInstance(null); }
        catch (error) { log("❌ Invalid OpenAI Key."); setOpenaiInstance(null); }
    }, [openaiKey, log]);

    useEffect(() => {
        const pfp = new Image(); pfp.crossOrigin = "anonymous"; pfp.src = profileImageSrc;
        pfp.onload = () => setProfileImage(pfp); pfp.onerror = () => setProfileImage(null);

        const backIcon = new Image(); backIcon.src = '/textAppImg/back_button_white.svg';
        backIcon.onload = () => setHeaderIcons(p => ({...p, back: backIcon}));
        const camIcon = new Image(); camIcon.src = '/textAppImg/video_camera_white.svg';
        camIcon.onload = () => setHeaderIcons(p => ({...p, camera: camIcon}));
    }, [profileImageSrc]);

    const applyPreset = (mode) => {
        const preset = mode === 'light' ? {
            headerColor: '#F0F0F0', chatBg: '#FFFFFF', titleText: '#000000', text: '#000000', sent: '#0B93F6', received: '#E5E7EB',
        } : {
            headerColor: '#1C1C1E', chatBg: '#000000', titleText: '#FFFFFF', text: '#FFFFFF', sent: '#0B93F6', received: '#3A3A3C',
        };
        setColorsData({...preset, svgIcon: '#0B93F6'});
        log(`🎨 Applied ${mode} mode preset.`);
    };

    const handleScriptUpload = (e) => {
        const file = e.target.files[0]; if (!file) return;
        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const loaded = JSON.parse(event.target.result);
                const newScript = loaded.map(item => ({
                    id: newId(), type: "text", text: Object.values(item)[0], sender: Object.keys(item)[0] === "Me" ? "You" : "Friend",
                    delay: 1500, voice: Object.keys(item)[0] === "Me" ? "alloy" : "shimmer", collapsed: true, speed: 1.0,
                    vibe: { ...vibePresets["No Vibe"], preset: "No Vibe", open: false }
                }));
                setScriptData(newScript); log(`✅ Script with ${newScript.length} messages loaded!`);
            } catch (error) { log(`❌ Failed to parse script. ${error.message}`); }
        };
        reader.readAsText(file); e.target.value = '';
    };

    const preloadAsset = useCallback(async (key, promise) => {
        if (preloadedAudio.current.has(key)) return preloadedAudio.current.get(key);
        const result = await promise;
        if(result) preloadedAudio.current.set(key, result);
        return result;
    }, []);

    const preloadImage = useCallback((src) => {
        if (!src || preloadedImages.current.has(src)) return Promise.resolve(preloadedImages.current.get(src));
        return new Promise((resolve) => {
            const img = new Image(); img.crossOrigin = "anonymous";
            img.onload = () => { preloadedImages.current.set(src, img); resolve(img); };
            img.onerror = () => { log(`❌ Failed to load image: ${src.substring(0, 50)}...`); resolve(null); };
            img.src = src;
        });
    }, [log]);

    const fetchOpenAITTS = useCallback(async (text, voice, vibe, speed) => {
        const instructions = createInstructionsFromVibe(vibe);
        const key = `tts:${voice}:${speed}:${instructions || ''}:${text}`;

        return preloadAsset(key, (async () => {
            if (!openaiInstance) { log("TTS Skipped: No OpenAI Key"); return null; }
            log(`Generating TTS for: "${text.substring(0, 20)}..." at ${speed}x speed`);
            if (instructions) log(`With instructions: ${instructions.replace(/\n\n/g, ' | ')}`);

            try {
                const response = await openaiInstance.audio.speech.create({
                    model: "gpt-4o-mini-tts",
                    input: text,
                    voice,
                    speed: speed || 1.0,
                    instructions: instructions,
                    response_format: "mp3"
                });
                const blob = await response.blob();
                const duration = await audioDuration(blob);
                return { blob, duration };
            } catch (error) { log(`❌ TTS Error: ${error.message}`); return null; }
        })());
    }, [openaiInstance, log, preloadAsset]);

    const fetchSFXData = useCallback(async (src) => {
        const key = `sfx:${src}`;
        return preloadAsset(key, (async () => {
            try {
                const response = await fetch(`/sounds/${src}`);
                if (!response.ok) throw new Error(`Status ${response.status}`);
                const blob = await response.blob();
                const duration = await audioDuration(blob);
                return { blob, duration };
            } catch (error) { log(`❌ SFX Error: ${src} - ${error.message}`); return null; }
        })());
    }, [log, preloadAsset]);

    const drawRoundRect = (ctx, x, y, width, height, radii) => {
        ctx.beginPath(); ctx.moveTo(x + radii.tl, y); ctx.lineTo(x + width - radii.tr, y);
        ctx.arcTo(x + width, y, x + width, y + radii.tr, radii.tr); ctx.lineTo(x + width, y + height - radii.br);
        ctx.arcTo(x + width, y + height, x + width - radii.br, y + height, radii.br); ctx.lineTo(x + radii.bl, y + height);
        ctx.arcTo(x, y + height, x, y + height - radii.bl, radii.bl); ctx.lineTo(x, y + radii.tl);
        ctx.arcTo(x, y, x + radii.tl, y, radii.tl); ctx.closePath(); ctx.fill();
    };

    const drawTintedImage = (ctx, image, color, x, y, width, height) => {
        if (!image || !image.complete || image.naturalWidth === 0) return;
        const offscreenCanvas = document.createElement('canvas');
        const offscreenCtx = offscreenCanvas.getContext('2d');
        offscreenCanvas.width = width; offscreenCanvas.height = height;
        offscreenCtx.fillStyle = color; offscreenCtx.fillRect(0, 0, width, height);
        offscreenCtx.globalCompositeOperation = 'destination-in';
        offscreenCtx.drawImage(image, 0, 0, width, height); ctx.drawImage(offscreenCanvas, x, y);
    };

    const calculateRenderedHeight = useCallback((messagesToMeasure) => {
        const canvas = canvasRef.current;
        if (!canvas) return 0;

        const ctx = document.createElement('canvas').getContext('2d');
        const targetWidth = 1080;
        const messageAreaW = targetWidth * (phoneUiWidth / 100);
        const baseFontSize = 28 * currentFontSizeMultiplier;
        const marginY = 20;

        const wrapText = (text, maxWidth) => {
            ctx.font = `${baseFontSize}px sans-serif`;
            const words = text.split(' ');
            let lines = [];
            let currentLine = words[0] || '';
            for (let i = 1; i < words.length; i++) {
                let testLine = currentLine + " " + words[i];
                if (ctx.measureText(testLine).width > maxWidth && i > 0) {
                    lines.push(currentLine);
                    currentLine = words[i];
                } else {
                    currentLine = testLine;
                }
            }
            lines.push(currentLine);
            return lines;
        };

        let totalContentHeight = 0;
        messagesToMeasure.forEach(msg => {
            if (msg.type === 'text' && msg.text) {
                const maxTextWidth = messageAreaW * 0.7;
                const lines = wrapText(msg.text, maxTextWidth);
                const textHeight = lines.length * (baseFontSize * 1.2);
                const bubbleHeight = textHeight + (baseFontSize * 1.2);
                totalContentHeight += bubbleHeight + marginY;
            } else if (msg.type === 'image' && msg.src) {
                const img = preloadedImages.current.get(msg.src);
                if (img) {
                    const imagePadding = 20;
                    const imgWidth = (messageAreaW * 0.6) * imageSizeMultiplier;
                    const imgHeight = img.height * (imgWidth / img.width);
                    const bubbleHeight = imgHeight + imagePadding;
                    totalContentHeight += bubbleHeight + marginY;
                }
            }
        });
        return totalContentHeight;
    }, [phoneUiWidth, currentFontSizeMultiplier, imageSizeMultiplier]);

    const drawAnimatorCanvas = useCallback((displayedMessages, activeAudioMsgIndex = -1, overrideUiHeight = null) => {
        const canvas = canvasRef.current; if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const targetWidth = 1080; const targetHeight = 1920;
        const scaleFactor = canvas.width / targetWidth;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = appBackgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.save(); ctx.scale(scaleFactor, scaleFactor);

        const chatRectWidth = targetWidth * (phoneUiWidth / 100);
        const chatRectHeight = overrideUiHeight !== null ? overrideUiHeight : targetHeight * (phoneUiHeight / 100);
        const chatRectX = (targetWidth - chatRectWidth) / 2;
        const yOffset = targetHeight * (phoneUiOffsetY / 100);
        const chatRectY = (targetHeight * 0.06) + yOffset;
        const chatRectRadius = 45;

        ctx.save();
        ctx.shadowColor = 'rgba(0, 0, 0, 0.4)'; ctx.shadowBlur = 20; ctx.shadowOffsetY = 10;
        ctx.fillStyle = colorsData.chatBg;
        drawRoundRect(ctx, chatRectX, chatRectY, chatRectWidth, chatRectHeight, { tl: chatRectRadius, tr: chatRectRadius, br: chatRectRadius, bl: chatRectRadius });
        ctx.restore();

        const basePfpSize = chatRectWidth * 0.07; const pfpSize = basePfpSize * pfpSizeMultiplier;
        const titleFontSize = Math.floor(chatRectWidth * 0.04);
        const baseIconSize = chatRectWidth * 0.05; const iconSize = baseIconSize * iconSizeMultiplier;
        const headerHeight = basePfpSize + (titleFontSize * 1.5) + (headerPaddingScale / 100 * 50);
        ctx.fillStyle = colorsData.headerColor;
        drawRoundRect(ctx, chatRectX, chatRectY, chatRectWidth, headerHeight, { tl: chatRectRadius, tr: chatRectRadius, br: 0, bl: 0 });
        const iconPaddingX = 40; const contentY = chatRectY + headerHeight / 2 + (iconOffsetY);

        if(headerIcons.back) drawTintedImage(ctx, headerIcons.back, colorsData.svgIcon, chatRectX + iconPaddingX, contentY - iconSize / 2, iconSize, iconSize);

        if (showHeaderBubble && headerBubbleText) {
            ctx.save();
            const bubbleFontSize = Math.floor(chatRectWidth * 0.035);
            ctx.font = `600 ${bubbleFontSize}px sans-serif`;
            const textMetrics = ctx.measureText(headerBubbleText);
            const textWidth = textMetrics.width;

            const bubblePaddingX = bubbleFontSize;
            const bubblePaddingY = bubbleFontSize * 0.5;
            const bubbleWidth = textWidth + (bubblePaddingX * 2);
            const bubbleHeight = bubbleFontSize + (bubblePaddingY * 2);
            const bubbleRadius = bubbleHeight / 2;

            const bubbleX = chatRectX + iconPaddingX + iconSize + 15 + headerBubbleOffsetX;
            const bubbleY = contentY - bubbleHeight / 2;

            ctx.fillStyle = headerBubbleColor;
            drawRoundRect(ctx, bubbleX, bubbleY, bubbleWidth, bubbleHeight, { tl: bubbleRadius, tr: bubbleRadius, br: bubbleRadius, bl: bubbleRadius });

            ctx.fillStyle = colorsData.text;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(headerBubbleText, bubbleX + bubbleWidth / 2, bubbleY + bubbleHeight / 2 + 1);
            ctx.restore();
        }

        if(headerIcons.camera) drawTintedImage(ctx, headerIcons.camera, colorsData.svgIcon, chatRectX + chatRectWidth - iconPaddingX - iconSize, contentY - iconSize / 2, iconSize, iconSize);
        const pfpY = chatRectY + (headerHeight - pfpSize - titleFontSize * 1.5) / 2;
        const pfpX = chatRectX + (chatRectWidth - pfpSize) / 2;

        if (profileImage) {
            ctx.save();
            ctx.beginPath();
            ctx.arc(pfpX + pfpSize / 2, pfpY + pfpSize / 2, pfpSize / 2, 0, Math.PI * 2);
            ctx.clip();

            const imgWidth = profileImage.naturalWidth;
            const imgHeight = profileImage.naturalHeight;
            const pfpRatio = 1;
            const imgRatio = imgWidth / imgHeight;

            let dWidth, dHeight, dx, dy;

            if (imgRatio > pfpRatio) {
                dHeight = pfpSize;
                dWidth = dHeight * imgRatio;
                dx = pfpX - (dWidth - pfpSize) / 2;
                dy = pfpY;
            } else {
                dWidth = pfpSize;
                dHeight = dWidth / imgRatio;
                dx = pfpX;
                dy = pfpY - (dHeight - pfpSize) / 2;
            }

            ctx.drawImage(profileImage, dx, dy, dWidth, dHeight);
            ctx.restore();
        }

        ctx.fillStyle = colorsData.titleText; ctx.font = `600 ${titleFontSize}px sans-serif`;
        ctx.textAlign = "center"; ctx.textBaseline = "middle";
        ctx.fillText(chatTitle, chatRectX + chatRectWidth / 2, pfpY + pfpSize + (titleFontSize * 0.8));

        const messageAreaX = chatRectX; const messageAreaY = chatRectY + headerHeight;
        const messageAreaW = chatRectWidth; const messageAreaH = chatRectHeight - headerHeight;
        const baseFontSize = 28 * currentFontSizeMultiplier; const marginY = 20;
        const wrapText = (text, maxWidth) => {
            ctx.font = `${baseFontSize}px sans-serif`;
            const words = text.split(' '); let lines = []; let currentLine = words[0] || '';
            for (let i = 1; i < words.length; i++) {
                let testLine = currentLine + " " + words[i];
                if (ctx.measureText(testLine).width > maxWidth && i > 0) { lines.push(currentLine); currentLine = words[i]; }
                else { currentLine = testLine; }
            } lines.push(currentLine); return lines;
        };

        let totalContentHeight = 0;
        displayedMessages.forEach(msg => {
            const imagePadding = 20;
            if (msg.type === 'text' && msg.text) {
                const maxTextWidth = messageAreaW * 0.7;
                const lines = wrapText(msg.text, maxTextWidth);
                const textHeight = lines.length * (baseFontSize * 1.2);
                const bubbleHeight = textHeight + (baseFontSize * 1.2);
                totalContentHeight += bubbleHeight + marginY;
            } else if (msg.type === 'image' && msg.src) {
                const img = preloadedImages.current.get(msg.src);
                if (img) {
                    const imgWidth = (messageAreaW * 0.6) * imageSizeMultiplier;
                    const imgHeight = img.height * (imgWidth / img.width);
                    const bubbleHeight = imgHeight + imagePadding;
                    totalContentHeight += bubbleHeight + marginY;
                }
            }
        });
        const scrollOffset = Math.max(0, totalContentHeight - messageAreaH + marginY);

        ctx.save();
        ctx.beginPath(); ctx.rect(messageAreaX, messageAreaY, messageAreaW, messageAreaH); ctx.clip();

        let currentY = messageAreaY + marginY - scrollOffset;
        const marginX = 30;

        displayedMessages.forEach((msg, index) => {
            if(currentY > messageAreaY + messageAreaH) return;
            const isSent = msg.sender?.toLowerCase() === "you";
            const dynamicRadius = bubbleRoundness;
            const radii = { tl: dynamicRadius, tr: dynamicRadius, br: dynamicRadius, bl: dynamicRadius };

            if (msg.type === 'text' && msg.text) {
                const maxTextWidth = messageAreaW * 0.7;
                const lines = wrapText(msg.text, maxTextWidth);
                const textWidth = Math.max(...lines.map(line => ctx.measureText(line).width));
                const textHeight = lines.length * (baseFontSize * 1.2);
                const bubbleHeight = textHeight + (baseFontSize * 1.2);
                const bubbleWidth = textWidth + (baseFontSize * 1.5);
                const x = isSent ? messageAreaX + messageAreaW - bubbleWidth - marginX : messageAreaX + marginX;
                if (currentY + bubbleHeight > messageAreaY) {
                    ctx.fillStyle = isSent ? colorsData.sent : colorsData.received;
                    drawRoundRect(ctx, x, currentY, bubbleWidth, bubbleHeight, radii);
                    ctx.fillStyle = colorsData.text; ctx.textAlign = 'left'; ctx.textBaseline = "top";
                    const textBlockPaddingY = (bubbleHeight - textHeight) / 2;
                    const textBlockStartY = currentY + textBlockPaddingY;
                    lines.forEach((line, i) => ctx.fillText(line, x + (baseFontSize * 0.75), textBlockStartY + (i * baseFontSize * 1.2)));
                    if (showSpeakerIcon && index === activeAudioMsgIndex) {
                        ctx.font = `${baseFontSize * 0.9}px sans-serif`;
                        ctx.textBaseline = "middle";
                        ctx.fillText("🔊", isSent ? x - (baseFontSize * 1.5) : x + bubbleWidth + 10, currentY + bubbleHeight / 2);
                    }
                }
                currentY += bubbleHeight + marginY;
            } else if (msg.type === 'image' && msg.src) {
                const img = preloadedImages.current.get(msg.src);
                if (img) {
                    const imagePadding = 20;
                    const imgWidth = (messageAreaW * 0.6) * imageSizeMultiplier;
                    const imgHeight = img.height * (imgWidth / img.width);
                    const bubbleWidth = imgWidth + imagePadding;
                    const bubbleHeight = imgHeight + imagePadding;
                    const bubbleX = isSent ? messageAreaX + messageAreaW - bubbleWidth - marginX : messageAreaX + marginX;

                    if (currentY + bubbleHeight > messageAreaY) {
                        ctx.fillStyle = isSent ? colorsData.sent : colorsData.received;
                        drawRoundRect(ctx, bubbleX, currentY, bubbleWidth, bubbleHeight, radii);

                        const imageX = bubbleX + imagePadding / 2;
                        const imageY = currentY + imagePadding / 2;

                        ctx.save();
                        const imageRadii = { tl: dynamicRadius-5, tr: dynamicRadius-5, br: dynamicRadius-5, bl: dynamicRadius-5 };
                        drawRoundRect(ctx, imageX, imageY, imgWidth, imgHeight, imageRadii);
                        ctx.clip();
                        ctx.drawImage(img, imageX, imageY, imgWidth, imgHeight);
                        ctx.restore();
                    }
                    currentY += bubbleHeight + marginY;
                }
            }
        });
        ctx.restore(); ctx.restore();
    }, [appBackgroundColor, colorsData, profileImage, headerIcons, chatTitle, phoneUiWidth, phoneUiHeight, phoneUiOffsetY, headerPaddingScale, iconOffsetY, bubbleRoundness, currentFontSizeMultiplier, showSpeakerIcon, pfpSizeMultiplier, iconSizeMultiplier, imageSizeMultiplier, showHeaderBubble, headerBubbleText, headerBubbleColor, headerBubbleOffsetX]);

    useEffect(() => {
        const allMessages = scriptData.filter(msg => msg.type !== 'soundEffect');
        drawAnimatorCanvas(allMessages);
    }, [drawAnimatorCanvas, scriptData]);

    const stopAnimation = () => { if(animationAbortController.current) { animationAbortController.current.abort(); animationAbortController.current = null; } };

    const runAnimation = useCallback(async ({ onFrame, onAudio }) => {
        stopAnimation();
        animationAbortController.current = new AbortController();
        const { signal } = animationAbortController.current;
        let displayedMessages = [];

        const targetWidth = 1080;
        const targetHeight = 1920;
        const chatRectWidth = targetWidth * (phoneUiWidth / 100);
        const basePfpSize = chatRectWidth * 0.07;
        const titleFontSize = Math.floor(chatRectWidth * 0.04);
        const headerHeight = basePfpSize + (titleFontSize * 1.5) + (headerPaddingScale / 100 * 50);
        const maxUiHeight = targetHeight * (phoneUiHeight / 100);

        for (let i = 0; i < scriptData.length; i++) {
            if (signal.aborted) break;
            const msg = scriptData[i];
            setProcessingStatus(`Processing ${i + 1}/${scriptData.length}`);

            if (msg.type === 'soundEffect') {
                const sfxData = await fetchSFXData(msg.src);
                if (sfxData?.blob) { if (onAudio) await onAudio(sfxData.blob); else await playAudioFromBlob(sfxData.blob, true); }
                else { await sleep(Number(msg.delay) || 1000); }
                continue;
            }

            const isSentMessage = msg.sender?.toLowerCase() === "you";
            const sfxKey = isSentMessage ? 'iphoneSwoosh.mp3' : 'dingsfx.mp3';
            const sfxPromise = playSendSFX ? fetchSFXData(sfxKey) : Promise.resolve(null);
            const ttsPromise = (msg.type === 'text' && msg.text) ? fetchOpenAITTS(msg.text, msg.voice, msg.vibe, msg.speed) : Promise.resolve(null);
            const imagePromise = preloadImage(msg.src);
            const [sfxData, ttsData] = await Promise.all([sfxPromise, ttsPromise]);
            await imagePromise;
            if (signal.aborted) break;

            let sfxPlaybackPromise = Promise.resolve();
            let ttsPlaybackPromise = Promise.resolve();

            if (isSentMessage && sfxData?.blob) {
                if (onAudio) {
                    sfxPlaybackPromise = onAudio(sfxData.blob);
                } else {
                    playAudioFromBlob(sfxData.blob);
                }
                await sleep(100);
            }

            displayedMessages.push(msg);

            const contentHeight = calculateRenderedHeight(displayedMessages);
            const bottomPadding = 40;
            const desiredUiHeight = headerHeight + contentHeight + bottomPadding;
            const currentUiHeight = Math.min(maxUiHeight, desiredUiHeight);

            drawAnimatorCanvas(displayedMessages, -1, currentUiHeight);
            if (onFrame) await onFrame();

            if (!isSentMessage && sfxData?.blob) {
                 if (onAudio) { sfxPlaybackPromise = onAudio(sfxData.blob); }
                 else { playAudioFromBlob(sfxData.blob); }
            }

            if (showSpeakerIcon && ttsData) {
                drawAnimatorCanvas(displayedMessages, displayedMessages.length - 1, currentUiHeight);
                if (onFrame) await onFrame();
            }

            if (onAudio) {
                ttsPlaybackPromise = ttsData?.blob ? onAudio(ttsData.blob) : Promise.resolve();
                await Promise.all([sfxPlaybackPromise, ttsPlaybackPromise]);
            } else {
                if (ttsData?.blob) { await playAudioFromBlob(ttsData.blob, true); }
                else { await sleep(Number(msg.delay) || 1000); }
            }

            if (showSpeakerIcon && ttsData) {
                drawAnimatorCanvas(displayedMessages, -1, currentUiHeight);
                if (onFrame) await onFrame();
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scriptData, playSendSFX, fetchSFXData, preloadImage, drawAnimatorCanvas, fetchOpenAITTS, showSpeakerIcon, calculateRenderedHeight, phoneUiWidth, pfpSizeMultiplier, headerPaddingScale, phoneUiHeight]);

    // ✨ NEW: Handler to preview a single sound effect
    const handleSfxPreview = async (sfxSrc, msgId) => {
        if (previewingId) return; // Prevent multiple previews at once
        setPreviewingId(msgId);
        log(`🔊 Previewing SFX: ${sfxSrc}`);
        try {
            const sfxData = await fetchSFXData(sfxSrc);
            if (sfxData?.blob) {
                await playAudioFromBlob(sfxData.blob, true);
            } else {
                log(`❌ Could not fetch SFX preview for ${sfxSrc}.`);
            }
        } catch (error) {
            log(`❌ Error playing SFX preview: ${error.message}`);
        }
        setPreviewingId(null);
    };

    // ✨ NEW: Handler to preview a single TTS voice line
    const handleTtsPreview = async (msg) => {
        if (previewingId) return; // Prevent multiple previews at once
        if (!msg.text?.trim()) {
            log("💬 Cannot preview empty text.");
            return;
        }
        if (!openaiInstance) {
            log("❌ OpenAI key not set. Cannot preview TTS.");
            return;
        }
        setPreviewingId(msg.id);
        log(`🔊 Previewing TTS for: "${msg.text.substring(0, 20)}..."`);
        try {
            const ttsData = await fetchOpenAITTS(msg.text, msg.voice, msg.vibe, msg.speed);
            if (ttsData?.blob) {
                await playAudioFromBlob(ttsData.blob, true);
            } else {
                log(`❌ Could not generate TTS preview.`);
            }
        } catch (error) {
            log(`❌ Error playing TTS preview: ${error.message}`);
        }
        setPreviewingId(null);
    };

    const handlePlayPreview = () => {
        setIsProcessing(true); setProcessingStatus('Playing Preview...'); log("▶️ Starting Preview...");
        runAnimation({}).finally(() => {
            setIsProcessing(false); setProcessingStatus(''); log("✅ Preview Finished.");
            drawAnimatorCanvas(scriptData.filter(m=>m.type!=='soundEffect'));
        });
    };

    const handleExportVideo = async () => {
        stopAnimation(); setIsProcessing(true); log("🚀 Starting Export...");
        const canvas = canvasRef.current;
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const destination = audioContext.createMediaStreamDestination();
        const stream = canvas.captureStream(30);
        if (destination.stream.getAudioTracks().length > 0) { stream.addTrack(destination.stream.getAudioTracks()[0]); }
        const recorder = new MediaRecorder(stream, { mimeType: 'video/webm;codecs=vp9,opus', videoBitsPerSecond: 8000000 });
        const chunks = [];
        recorder.ondataavailable = e => chunks.push(e.data);
        recorder.onstop = () => {
            const blob = new Blob(chunks, { type: 'video/webm' });
            const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'chat-animation.webm';
            a.click(); URL.revokeObjectURL(a.href);
            log("✅ Video download started!"); audioContext.close();
            drawAnimatorCanvas(scriptData.filter(m=>m.type!=='soundEffect'));
        };
        const playAudioForRecording = async (blob) => {
            try {
                const arrayBuffer = await blob.arrayBuffer();
                const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
                const source = audioContext.createBufferSource();
                source.buffer = audioBuffer; source.connect(destination); source.connect(audioContext.destination);
                await new Promise(resolve => { source.onended = resolve; source.start(); });
            } catch (e) { log(`❌ Audio error during recording: ${e.message}`); }
        };
        recorder.start();
        await runAnimation({ onFrame: () => new Promise(resolve => requestAnimationFrame(resolve)), onAudio: playAudioForRecording });
        recorder.stop(); setIsProcessing(false);
    };

    const onDragEnd = (result) => { if (!result.destination) return; const items = [...scriptData]; const [reorderedItem] = items.splice(result.source.index, 1); items.splice(result.destination.index, 0, reorderedItem); setScriptData(items); };
    const addMessage = (index, type) => {
        let newMsg;
        if (type === 'image') { newMsg = { id: newId(), type: "image", src: "", sender: "You", delay: 2500, collapsed: false }; }
        else if (type === 'sfx') { newMsg = { id: newId(), type: "soundEffect", src: availableSoundEffects[0].value, delay: 1000, collapsed: false }; }
        else { newMsg = { id: newId(), type: "text", text: "", sender: "You", delay: 1000, voice: "alloy", collapsed: false, speed: 1.0, vibe: { ...vibePresets["No Vibe"], preset: "No Vibe", open: false } }; }
        const newArr = [...scriptData]; newArr.splice(index + 1, 0, newMsg); setScriptData(newArr);
    };
    const deleteMessage = (idToDelete) => { setScriptData(p => p.filter((msg) => msg.id !== idToDelete)); };

    const handleVibePresetChange = (index, presetName) => { const newVibe = vibePresets[presetName] || vibePresets["No Vibe"]; setScriptData(p => p.map((m, i) => i === index ? { ...m, vibe: { ...newVibe, preset: presetName, open: m.vibe.open } } : m)); };
    const handleVibeDetailChange = (index, key, value) => { setScriptData(p => p.map((m, i) => i === index ? { ...m, vibe: { ...m.vibe, [key]: value, preset: "Custom" } } : m)); };
    const toggleVibeDetails = (index) => { setScriptData(p => p.map((m, i) => i === index ? { ...m, vibe: { ...m.vibe, open: !m.vibe.open } } : m)); };

    return (
        <div className="chat-animator-container">
            <h1 className="page-title">Chat Animator</h1>
            <div className="main-grid">
                {/* --- CONTROLS COLUMN --- */}
                <div className="controls-column">
                     <div className="control-section"> <h2 className="section-title">Global Settings</h2> <div className="grid grid-cols-2 gap-4 mb-4">
                           <ColorInput id="app_bg" label="App BG" value={appBackgroundColor} onChange={e => setAppBackgroundColor(e.target.value)} />
                           <ColorInput id="header_bg" label="Header BG" value={colorsData.headerColor} onChange={e => setColorsData({...colorsData, headerColor: e.target.value})} />
                           <ColorInput id="chat_bg" label="Chat BG" value={colorsData.chatBg} onChange={e => setColorsData({...colorsData, chatBg: e.target.value})} />
                           <ColorInput id="sent_bg" label="Sent Bubble" value={colorsData.sent} onChange={e => setColorsData({...colorsData, sent: e.target.value})} />
                           <ColorInput id="rcvd_bg" label="Rcvd Bubble" value={colorsData.received} onChange={e => setColorsData({...colorsData, received: e.target.value})} />
                           <ColorInput id="text_color" label="Text Color" value={colorsData.text} onChange={e => setColorsData({...colorsData, text: e.target.value, titleText: e.target.value})}/>
                           <ColorInput id="icon_color" label="Header Icons" value={colorsData.svgIcon} onChange={e => setColorsData({...colorsData, svgIcon: e.target.value})}/>
                        </div> <div className="button-group"><button onClick={() => applyPreset('light')} className="flex-1 button-secondary">☀️ Light</button><button onClick={() => applyPreset('dark')} className="flex-1 button-secondary">🌙 Dark</button></div>
                    </div>
                    <div className="control-section"> <h2 className="section-title">Phone UI Controls</h2>
                         <TextInput id="chat_title_text" label="Chat Title" value={chatTitle} onChange={e => setChatTitle(e.target.value)} />
                         <div className="mt-2"><label className="label">Profile Image</label><input type="file" accept="image/*" onChange={(e) => { const f = e.target.files[0]; if(f) setProfileImageSrc(URL.createObjectURL(f)); }} className="file-input"/></div>
                         <RangeInput id="pfp_size" label="PFP Size" value={pfpSizeMultiplier} min="0.5" max="2" step="0.05" onChange={e => setPfpSizeMultiplier(parseFloat(e.target.value))} unit="x" />
                         <RangeInput id="icon_size" label="Icon Size" value={iconSizeMultiplier} min="0.5" max="2" step="0.05" onChange={e => setIconSizeMultiplier(parseFloat(e.target.value))} unit="x" />
                         <RangeInput id="font_size" label="Font Size" value={currentFontSizeMultiplier} min="0.5" max="2" step="0.05" onChange={e => setCurrentFontSizeMultiplier(parseFloat(e.target.value))} unit="x" />
                         <RangeInput id="img_size" label="Image Size" value={imageSizeMultiplier} min="0.2" max="1.2" step="0.05" onChange={e => setImageSizeMultiplier(parseFloat(e.target.value))} unit="x" />
                         <RangeInput id="bubble_round" label="Bubble Roundness" value={bubbleRoundness} min="0" max="50" onChange={e => setBubbleRoundness(parseInt(e.target.value))} unit="px"/>
                         <RangeInput id="phone_width" label="UI Width" value={phoneUiWidth} min="50" max="100" onChange={e => setPhoneUiWidth(parseInt(e.target.value))} unit="%"/>
                         <RangeInput id="phone_height" label="UI Height" value={phoneUiHeight} min="40" max="90" onChange={e => setPhoneUiHeight(parseInt(e.target.value))} unit="%"/>
                         <RangeInput id="phone_offset" label="Vertical Offset" value={phoneUiOffsetY} min="-20" max="20" onChange={e => setPhoneUiOffsetY(parseInt(e.target.value))} unit="%"/>
                         <RangeInput id="header_pad" label="Header Padding" value={headerPaddingScale} min="50" max="150" onChange={e => setHeaderPaddingScale(parseInt(e.target.value))} unit="%"/>
                         <RangeInput id="icon_offset" label="Icon Offset" value={iconOffsetY} min="-20" max="20" onChange={e => setIconOffsetY(parseInt(e.target.value))} unit="px"/>
                    </div>
                    <div className="control-section">
                        <h2 className="section-title">Header Bubble</h2>
                        <div className="flex items-center gap-2 mb-2">
                            <input id="header-bubble-toggle" type="checkbox" checked={showHeaderBubble} onChange={() => setShowHeaderBubble(p => !p)} className="h-4 w-4"/>
                            <label htmlFor="header-bubble-toggle" className="text-sm">Show Header Bubble</label>
                        </div>
                        {showHeaderBubble && (
                            <>
                                <TextInput id="header_bubble_text" label="Bubble Text" value={headerBubbleText} onChange={e => setHeaderBubbleText(e.target.value)} />
                                <ColorInput id="header_bubble_color" label="Bubble Color" value={headerBubbleColor} onChange={e => setHeaderBubbleColor(e.target.value)} />
                                <RangeInput
                                    id="header_bubble_offset_x"
                                    label="Horizontal Offset"
                                    value={headerBubbleOffsetX}
                                    min="-50"
                                    max="150"
                                    step="1"
                                    onChange={e => setHeaderBubbleOffsetX(Number(e.target.value))}
                                    unit="px"
                                />
                            </>
                        )}
                    </div>
                    <div className="control-section"> <h2 className="section-title">Controls & Export</h2>
                        <div className="flex items-center gap-2 mb-2"><input id="icon-toggle" type="checkbox" checked={showSpeakerIcon} onChange={() => setShowSpeakerIcon(p => !p)} className="h-4 w-4"/><label htmlFor="icon-toggle" className="text-sm">Show 🔊 Icon during TTS</label></div>
                        <div className="flex items-center gap-2 mb-4"><input id="fx-toggle" type="checkbox" checked={playSendSFX} onChange={() => setPlaySendSFX(p => !p)} className="h-4 w-4"/><label htmlFor="fx-toggle" className="text-sm">Play Message SFX</label></div>
                        <TextInput id="openai_key" label="OpenAI API Key" type="password" value={openaiKey} onChange={(e) => setOpenaiKey(e.target.value)} />
                        <div className="flex gap-2 mt-4">
                           <button onClick={isProcessing ? stopAnimation : handlePlayPreview} className="flex-1 button-secondary">{isProcessing ? 'Stop' : 'Play Preview'}</button>
                           <button onClick={handleExportVideo} disabled={isProcessing} className="flex-1 button-primary">{isProcessing ? processingStatus : 'Export Video'}</button>
                        </div>
                    </div>
                    <div className="control-section"><h2 className="section-title">Upload Script (.json)</h2><input type="file" accept=".json" onChange={handleScriptUpload} className="file-input"/></div>
                    <div className="control-section flex-grow"><h2 className="section-title">Logs</h2><div className="editor-log">{editorLog.map((l, i) => <p key={i}>{l}</p>)}</div></div>
                </div>

                {/* --- PREVIEW COLUMN --- */}
                <div className="preview-column"><div className="canvas-container"><canvas ref={canvasRef} width="720" height="1280"></canvas></div></div>

                {/* --- SCRIPT COLUMN --- */}
                <div className="script-column"> <h2 className="section-title">Edit Script</h2> <div className="script-editor-container">
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="script">{(provided) => (
                                <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-3">
                                    {scriptData.map((msg, idx) => (
                                        <Draggable key={msg.id} draggableId={msg.id.toString()} index={idx}>{(p) => (
                                            <div ref={p.innerRef} {...p.draggableProps} className="script-item" style={{ borderColor: msg.type === "soundEffect" ? "#facc15" : msg.type === "image" ? "#22c55e" : msg.collapsed ? "#4b5563" : "#38bdf8" }}>
                                                <div className="script-item-header">
                                                    <div {...p.dragHandleProps} className="drag-handle" title="Drag to reorder">⠿</div>
                                                    <button onClick={() => setScriptData(prev => prev.map((m,i) => i === idx ? {...m, collapsed: !m.collapsed} : m))} className="script-item-title-btn">
                                                        #{idx + 1} - {msg.type}: {msg.text?.slice(0, 15) || msg.src?.split('/').pop()?.slice(0,15) || 'Effect'}...
                                                    </button>
                                                    <div className="script-button-group">
                                                        <button title="Add Text Message Below" onClick={() => addMessage(idx, 'text')} className="script-header-btn" style={{backgroundColor: '#0284c7'}}>Msg</button>
                                                        <button title="Add Image Message Below" onClick={() => addMessage(idx, 'image')} className="script-header-btn" style={{backgroundColor: '#16a34a'}}>Img</button>
                                                        <button title="Add Sound Effect Below" onClick={() => addMessage(idx, 'sfx')} className="script-header-btn" style={{backgroundColor: '#eab308'}}>SFX</button>
                                                        <button title="Delete This Message" onClick={() => deleteMessage(msg.id)} className="script-header-btn" style={{backgroundColor: '#dc2626'}}>✕</button>
                                                    </div>
                                                </div>
                                                {!msg.collapsed && ( <div className="p-3 space-y-4 bg-slate-700/80">
                                                    {msg.type === "text" && (<>
                                                        <div><label className="label">Text</label><textarea value={msg.text} onChange={e => setScriptData(p => p.map((m,i)=>i===idx ? {...m, text: e.target.value}:m))} className="input-base min-h-[60px] !h-auto"/></div>
                                                        {/* ✨ MODIFIED: Added preview button next to voice selector */}
                                                        <div className="flex gap-2 items-end">
                                                            <SelectInput id={`sender-${idx}`} label="Sender" value={msg.sender} options={["You", "Friend"]} onChange={e => setScriptData(p=>p.map((m,i)=>i===idx ? {...m, sender: e.target.value}:m))}/>
                                                            <SelectInput id={`voice-${idx}`} label="Voice" value={msg.voice} options={voices} onChange={e => setScriptData(p=>p.map((m,i)=>i===idx ? {...m, voice: e.target.value}:m))}/>
                                                            <button
                                                                onClick={() => handleTtsPreview(msg)}
                                                                disabled={previewingId === msg.id || !openaiKey || !msg.text?.trim()}
                                                                className="button-secondary preview-btn"
                                                                title="Preview Voice (requires text & API key)"
                                                            >
                                                                {previewingId === msg.id ? '...' : '▶️'}
                                                            </button>
                                                        </div>

                                                        <RangeInput id={`speed-${idx}`} label="Voice Speed" value={msg.speed || 1.0} min="0.25" max="4.0" step="0.05"
                                                            onChange={e => setScriptData(p => p.map((m, i) => i === idx ? { ...m, speed: parseFloat(e.target.value) } : m))} unit="x" />

                                                        <div className="vibe-section">
                                                          <div className="flex justify-between items-center gap-2 mb-2">
                                                              <label className="label !mb-0">Voice Vibe</label>
                                                              <div className="flex gap-2 items-center">
                                                                  <select className="vibe-preset-select" value={msg.vibe.preset} onChange={(e) => handleVibePresetChange(idx, e.target.value)}>
                                                                      {Object.keys(vibePresets).map(name => <option key={name} value={name}>{name}</option>)}
                                                                  </select>
                                                                  <button onClick={() => toggleVibeDetails(idx)} className="text-xs text-sky-400 hover:underline">{msg.vibe.open ? "Hide" : "Show"}</button>
                                                              </div>
                                                          </div>
                                                          {msg.vibe.open && (
                                                            <div className="vibe-details-grid">
                                                                {(vibePresets["Custom"] ? Object.keys(vibePresets["Custom"]) : []).filter(k => k !== 'open' && k !== 'preset').map((key) => (
                                                                    <div key={key} className="flex flex-col">
                                                                        <label className="vibe-detail-label">{key.replace(/([A-Z])/g, ' $1')}</label>
                                                                        <input type="text" value={msg.vibe[key] || ""} onChange={(e) => handleVibeDetailChange(idx, key, e.target.value)} className="vibe-detail-input" />
                                                                    </div>
                                                                ))}
                                                            </div>
                                                          )}
                                                        </div>
                                                    </>)}
                                                    {msg.type === "image" && (<div className="space-y-2">
                                                        <SelectInput id={`sender-img-${idx}`} label="Sender" value={msg.sender} options={["You", "Friend"]} onChange={e => setScriptData(p=>p.map((m,i)=>i===idx ? {...m, sender: e.target.value}:m))}/>
                                                        <div><label className="label">Image</label><input type="file" accept="image/*" className="file-input" onChange={(e)=>{const f=e.target.files[0]; if(f) setScriptData(p=>p.map((m,i)=>i===idx ? {...m, src:URL.createObjectURL(f)}:m))}}/></div>
                                                        {msg.src && <img src={msg.src} alt="preview" className="h-16 w-16 mt-2 rounded object-cover"/>}
                                                    </div>)}
                                                    {/* ✨ MODIFIED: Added preview button next to SFX selector */}
                                                    {msg.type === "soundEffect" && (
                                                        <div className="flex gap-2 items-end">
                                                            <div className="flex-grow">
                                                                <SelectInput id={`sfx-${idx}`} label="Sound Effect" value={msg.src} options={availableSoundEffects} onChange={e => setScriptData(p=>p.map((m,i)=>i===idx ? {...m, src: e.target.value}:m))}/>
                                                            </div>
                                                            <button
                                                                onClick={() => handleSfxPreview(msg.src, msg.id)}
                                                                disabled={previewingId === msg.id}
                                                                className="button-secondary preview-btn"
                                                                title="Preview Sound"
                                                            >
                                                                {previewingId === msg.id ? '...' : '▶️'}
                                                            </button>
                                                        </div>
                                                    )}
                                                    <TextInput id={`delay-${idx}`} label="Delay After (ms)" type="number" value={msg.delay} onChange={e => setScriptData(p => p.map((m,i)=>i===idx ? {...m, delay:Number(e.target.value)}:m))}/>
                                                </div>)}
                                            </div>
                                        )}</Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}</Droppable>
                        </DragDropContext>
                    </div> <button onClick={() => addMessage(scriptData.length - 1, 'text')} className="mt-4 w-full button-secondary">+ Add New Message</button>
                </div>
            </div>
        </div>
    );
}
